import * as React from 'react';
import { useSelector } from 'react-redux';
import Flex from 'antd/lib/flex';
import Text from 'antd/lib/typography/Text';
import Divider from 'antd/lib/divider';
import Tooltip from 'antd/lib/tooltip';
import { MoonOutlined, PlusOutlined, SunOutlined } from '@ant-design/icons';
import HybridModeIcon from '../../icons/HybridModeIcon';
import { RootState } from '../../../reducers/reducers';
import { selectOrg, selectUserIsRunwayAdmin } from '../../../selectors/selectors';
import { AppGroup } from '../../../types';
import { getAppGroupsForOrgId } from '../../../helpers';
import { displayUserNameLong } from '../../../helpers/displayHelpers';
import { antGrey, getNavSectionTitleStyle } from '../../styles/globalStyles';
import { HoverableNavItem } from '../shared/HoverableNavItem';
import LogoWordmark from '../../icons/LogoWordmark';
import { useThemeMode, useSidebarColors, useSidebarTheme } from '../../styles/themeMode';
import Button from 'antd/lib/button';
import { AppGroupRow } from './AppGroupRow';
import { OrgMetricsSection, UserSection, InviteSection } from './ProductDropdownSections';

interface ReleaseManagementSectionProps {
    orgId: string;
    appGroups: AppGroup[] | null | undefined;
    currentAppGroupId: string | null | undefined;
    currentAppId: string | undefined;
    currentAppGroupAllAppNamesAreSame?: boolean;
    onAppGroupSelect: (appGroupId: string) => void;
    onPlatformSelect: (appEntityId: string, appGroupId: string) => void;
    onSettingsClick: (appIdParam: string) => void;
    onAddProduct: () => void;
}

const ReleaseManagementSection: React.FC<ReleaseManagementSectionProps> = ({
    orgId,
    appGroups,
    currentAppGroupId,
    currentAppId,
    currentAppGroupAllAppNamesAreSame,
    onAppGroupSelect,
    onPlatformSelect,
    onSettingsClick,
    onAddProduct,
}) => {
    const sc = useSidebarColors();
    if (!appGroups) return null;

    const filteredAppGroups = appGroups.filter((appGroup) => appGroup.appIds?.length > 0);

    return (
        <Flex vertical>
            <Text style={getNavSectionTitleStyle(sc)}>Release Management</Text>

            <div
                style={{
                    maxHeight: '30vh',
                    overflowY: 'auto',
                }}
            >
                {filteredAppGroups.map((appGroup) => (
                    <AppGroupRow
                        key={appGroup.id}
                        orgId={orgId}
                        appGroupId={appGroup.id}
                        isSelected={appGroup.id === currentAppGroupId}
                        currentAppId={currentAppId}
                        allAppNamesAreSame={
                            appGroup.id === currentAppGroupId ? currentAppGroupAllAppNamesAreSame : undefined
                        }
                        onSelect={() => onAppGroupSelect(appGroup.id)}
                        onPlatformSelect={(appEntityId) => onPlatformSelect(appEntityId, appGroup.id)}
                        onSettingsClick={onSettingsClick}
                    />
                ))}
            </div>

            <AddProductButton onClick={onAddProduct} />
        </Flex>
    );
};

const AddProductButton: React.FC<{ onClick: () => void }> = ({ onClick }) => {
    const sc = useSidebarColors();
    return (
        <HoverableNavItem onClick={onClick}>
            <Flex align="center" gap={12}>
                <Flex
                    align="center"
                    justify="center"
                    style={{
                        width: 32,
                        height: 32,
                        borderRadius: 6,
                        border: `1px solid ${sc.border}`,
                    }}
                >
                    <PlusOutlined style={{ color: sc.icon, fontSize: 16 }} />
                </Flex>
                <Text style={{ color: sc.textSecondary }}>Add a product...</Text>
            </Flex>
        </HoverableNavItem>
    );
};

export interface ProductDropdownProps {
    orgId: string;
    currentAppGroupId: string | null | undefined;
    currentAppId: string | undefined;
    currentAppGroupAllAppNamesAreSame?: boolean;
    onAppGroupSelect: (appGroupId: string) => void;
    onPlatformSelect: (appEntityId: string, appGroupId: string) => void;
    onSettingsClick: (appIdParam: string) => void;
    onAddProduct: () => void;
    onOrgClick: () => void;
    onUserClick: () => void;
    onInviteClick: () => void;
}

export const ProductDropdown: React.FC<ProductDropdownProps> = ({
    orgId,
    currentAppGroupId,
    currentAppId,
    currentAppGroupAllAppNamesAreSame,
    onAppGroupSelect,
    onPlatformSelect,
    onSettingsClick,
    onAddProduct,
    onOrgClick,
    onUserClick,
    onInviteClick,
}) => {
    const org = useSelector((state: RootState) => selectOrg(state, orgId));
    const user = useSelector((state: RootState) => state.user);
    const appGroups = useSelector((state: RootState) => getAppGroupsForOrgId(orgId, state));
    const isRunwayAdmin = useSelector((state: RootState) => selectUserIsRunwayAdmin(state));
    const { mode, setMode } = useThemeMode();
    const sc = useSidebarColors();
    const sidebarTheme = useSidebarTheme();

    if (!org) return null;

    const showInvite = !org.ssoSamlConfig?.ssoSamlEnforced;
    const orgLetter = org.name?.charAt(0).toUpperCase() || 'O';
    const userName = displayUserNameLong(user);
    const userLetter = userName.charAt(0).toUpperCase();

    return (
        <div
            style={{
                backgroundColor: sc.dropdownBg,
                borderRadius: 8,
                border: `1px solid ${sc.border}`,
                minWidth: '25rem',
            }}
        >
            <ReleaseManagementSection
                orgId={orgId}
                appGroups={appGroups}
                currentAppGroupId={currentAppGroupId}
                currentAppId={currentAppId}
                currentAppGroupAllAppNamesAreSame={currentAppGroupAllAppNamesAreSame}
                onAppGroupSelect={onAppGroupSelect}
                onPlatformSelect={onPlatformSelect}
                onSettingsClick={onSettingsClick}
                onAddProduct={onAddProduct}
            />

            <Divider style={{ borderColor: sc.border, margin: 0 }} />
            <OrgMetricsSection
                orgLetter={orgLetter}
                orgName={org.name}
                orgImageUrl={org.orgImageUrl}
                onOrgClick={onOrgClick}
            />

            <Divider style={{ borderColor: sc.border, margin: 0 }} />
            <UserSection
                userLetter={userLetter}
                userName={userName}
                userAvatarUrl={user.commsAvatars?.image48}
                onUserClick={onUserClick}
            />

            {showInvite && (
                <>
                    <Divider style={{ borderColor: sc.border, margin: 0 }} />
                    <InviteSection onInviteClick={onInviteClick} />
                </>
            )}

            <Flex justify="space-between" align="center" style={{ padding: '16px 20px' }}>
                {(() => {
                    const modeOrder =
                        isRunwayAdmin ? (['light', 'mixed', 'dark'] as const) : (['light', 'mixed'] as const);
                    const currentIndex = (modeOrder as readonly string[]).indexOf(mode);
                    const nextMode = modeOrder[(currentIndex + 1) % modeOrder.length];
                    const modeIcons = {
                        light: <SunOutlined style={{ fontSize: 20 }} />,
                        mixed: <HybridModeIcon size={20} />,
                        dark: <MoonOutlined style={{ fontSize: 20 }} />,
                    };
                    const modeLabels = {
                        light: 'Light mode',
                        mixed: 'Mixed mode',
                        dark: 'Dark mode',
                    };
                    return (
                        <Tooltip title={`Switch to ${modeLabels[nextMode].toLowerCase()}`}>
                            <Button
                                type="text"
                                aria-label={`Switch to ${modeLabels[nextMode].toLowerCase()}`}
                                onClick={() => {
                                    setMode(nextMode);
                                }}
                                style={{ padding: 0, height: 'auto', color: sc.text, justifyContent: 'start' }}
                                icon={modeIcons[nextMode]}
                            />
                        </Tooltip>
                    );
                })()}

                <LogoWordmark height={12} color={sidebarTheme === 'light' ? sc.textMuted : antGrey.grey1} />
            </Flex>
        </div>
    );
};
